import Plugin from '@ckeditor/ckeditor5-core/src/plugin';
import LinkEditing from '@ckeditor/ckeditor5-link/src/linkediting';
import LinkUI from '@ckeditor/ckeditor5-link/src/linkui';

export default class ExternalLinkTarget extends Plugin {
  static get pluginName() {
    return 'ExternalLinkTarget';
  }

  static get requires() {
    return [LinkEditing, LinkUI];
  }

  init() {
    const { model, conversion } = this.editor;

    const INTERNAL_DOMAINS = [
      'band.com.br',
      'bandplay.com',
      'bandshop.com.br',
      'lojabandfm.com.br',
      'lojamelhordatarde.com.br',
      'lojanativafm.com.br',
      'vibra.digital'
    ];

    const isExternalHref = href => {
      if (!href) {
        return false;
      }

      try {
        const host = new URL(href, 'https://dummy.base').hostname.toLowerCase();
        return !INTERNAL_DOMAINS.some(domain => host === domain || host.endsWith(`.${domain}`));
      } catch (err) {
        // If URL parsing fails, treat as external to stay safe.
        return true;
      }
    };
    this.isExternalHref = isExternalHref;

    model.schema.extend('$text', { allowAttributes: ['linkRel'] });

    const syncAllLinks = () => {
      model.change(writer => {
        for (const root of model.document.roots) {
          for (const { item } of model.createRangeIn(root)) {
            if (!item.getAttribute || !item.hasAttribute('linkHref')) {
              continue;
            }
            const href = item.getAttribute('linkHref');
            const external = isExternalHref(href);
            const current = item.getAttribute('linkIsExternal');

            if (external && current !== true) {
              writer.setAttribute('linkIsExternal', true, item);
              writer.setAttribute('linkRel', 'noopener noreferrer', item);
            }
          }
        }
      });
    };

    // After initial data is ready, mark existing links so the balloon checkbox reflects external ones.
    this.editor.data.on('ready', syncAllLinks);

    // Keep linkIsExternal in sync always, even if the user tries to uncheck it on external links.
    model.document.registerPostFixer(writer => {
      const changes = model.document.differ.getChanges();
      let applied = false;

      for (const change of changes) {
        if (change.type !== 'attribute' && change.type !== 'insert') {
          continue;
        }

        let range = null;

        if (change.type === 'attribute') {
          range = change.range;
        } else {
          const node = change.position.nodeAfter || change.position.nodeBefore;
          range = node ? model.createRangeOn(node) : null;
        }

        if (!range) {
          continue;
        }

        for (const item of range.getItems()) {
          if (!item.getAttribute) {
            continue;
          }

          const href = item.getAttribute('linkHref');
          const external = isExternalHref(href);
          const current = item.getAttribute('linkIsExternal');

          if (external && current !== true) {
            writer.setAttribute('linkIsExternal', true, item);
            writer.setAttribute('linkRel', 'noopener noreferrer', item);
            applied = true;
          }
        }
      }

      return applied;
    });

    const applyRel = pipeline => {
      pipeline.add(dispatcher => {
        dispatcher.on(
          'attribute:linkRel',
          (evt, data, conversionApi) => {
            const { mapper, writer } = conversionApi;
            const viewRange = mapper.toViewRange(data.range);

            for (const viewItem of viewRange.getItems()) {
              if (!viewItem.is('element', 'a')) {
                continue;
              }

              if (data.attributeNewValue) {
                writer.setAttribute('rel', data.attributeNewValue, viewItem);
              } else {
                writer.removeAttribute('rel', viewItem);
              }
            }
          },
          { priority: 'low' }
        );
      });
    };

    applyRel(conversion.for('dataDowncast'));
    applyRel(conversion.for('editingDowncast'));

    const linkUI = this.editor.plugins.get('LinkUI');
    if (linkUI) {
      // No-op: UI customization handled by LinkRelInfo plugin.
    }
  }
}
